import React, { useState, useRef } from "react";
import { Play, ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PrecisionDemolitionRapidHauling() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll();
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  return <section data-section-id="29951" id="stop-losing-leads" className="dark bg-background text-foreground relative overflow-hidden min-h-[85vh] flex flex-col lg:flex-row">
      <div className="absolute inset-0 z-0 pointer-events-none opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

      <div className="relative z-10 w-full lg:w-[60%] flex flex-col justify-center px-6 py-16 lg:px-16 xl:px-24">
        <div ref={textRef} className={`transition-all duration-1000 ease-out transform ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-bold tracking-wider uppercase mb-6">
            <span className="w-2 h-2 rounded-lg bg-primary animate-pulse"></span>
            UK's #1 Missed Call Recovery for Sole Traders & Tradespeople
          </div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.9] mb-8 text-white uppercase">
            STOPS MISSED <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-700">
              CALLS.
            </span>
            <br />
            SAVES YOUR <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">TRADE & SERVICE PROS.</span>
          </h1>

          <p className="text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed border-l-4 border-primary/50 pl-6 font-sans">
            The system that sends an instant WhatsApp to your missed calls.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Button asChild size="lg" className="h-14 px-8 text-base font-bold uppercase tracking-wide hover:shadow-[0_0_30px_rgba(255,140,0,0.5)] transition-all duration-300 bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/PricingPage">
                Secure Founder Spot <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="lg" className="h-14 px-6 text-base font-medium text-white hover:bg-white/5 hover:text-primary border border-white/10 group">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 text-primary mr-3 group-hover:bg-primary group-hover:text-white transition-colors">
                    <Play className="h-4 w-4 fill-current" />
                  </div>
                  See The Silent Operator
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-4xl bg-black/95 border-white/10 p-0 overflow-hidden">
                <DialogHeader className="sr-only">
                  <DialogTitle>The Silent Operator in Action</DialogTitle>
                  <DialogDescription>Video showing ThinkLocalPro's automated lead recovery.</DialogDescription>
                </DialogHeader>
                <div className="aspect-video w-full bg-black flex items-center justify-center relative">
                  <video ref={videoRef} src="https://assets.cdn.filesafe.space/L6l1t4NRjHM9xf3FjS05/media/69fcaaed10ebaf037eccc31e.mp4" className="w-full h-full rounded-lg" controls autoPlay playsInline />
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mt-12 flex flex-wrap gap-6 text-sm font-medium text-muted-foreground/80 font-sans">
            {["Built by UK Pros", "Instant WhatsApp Recovery", "No Long-Term Contracts"].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-primary" />
                {item}
              </div>)}
          </div>
        </div>
      </div>

      <div className="relative w-full lg:w-[40%] min-h-[50vh] lg:min-h-auto overflow-hidden">
        <div ref={imageRef} className={`absolute inset-0 w-full h-full transition-all duration-1000 delay-300 ease-out transform ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"}`}>
          <div className="absolute inset-0 w-full h-full lg:[clip-path:polygon(15%_0,100%_0,100%_100%,0%_100%)] z-10 bg-background">
             <div className="relative w-full h-full overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 mix-blend-overlay z-10"></div>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_tradesman_lead_recovery_unbranded.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_tradesman_lead_recovery_unbranded.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_tradesman_lead_recovery_unbranded.png-wvc-sizes" alt="Professional UK tradesman recovering a lead on a mobile device" className="w-full h-full object-cover object-center scale-105 hover:scale-110 transition-transform duration-700 ease-in-out brightness-75" loading="lazy" />
                
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/95 via-black/60 to-transparent z-20 lg:pl-24">
                  <div className="border-l-2 border-primary pl-4">
                    <p className="text-white font-bold text-lg uppercase tracking-tight">Status: Lead Recovered</p>
                    <p className="text-primary text-sm font-mono">Gloucester, UK</p>
                  </div>
                </div>
             </div>
          </div>
          
          <div className="hidden lg:block absolute top-10 left-0 w-32 h-32 border-t-2 border-l-2 border-primary/30 -translate-x-1/2"></div>
        </div>
      </div>
    </section>;
}